import {
    ArrowRightStartOnRectangleIcon,
    ChevronUpIcon,
    LightBulbIcon,
} from "@heroicons/react/16/solid";
import {
    ChatBubbleLeftRightIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    Cog8ToothIcon,
    TableCellsIcon,
    UserGroupIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import { ActionType } from "@sp-crm/core";
import { AddClientSidebar } from "components/clients/add-client-button/add-client-button";
import { Avatar } from "components/layout/avatar";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from "components/layout/dropdown";
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarItem,
    SidebarLabel,
    SidebarMode,
    SidebarSection,
    SidebarSpacer,
} from "components/layout/sidebar";
import { NgRegionToggle } from "components/region-toggle/ng-region-toggle";
import { getInitials } from "components/ui/persona";
import { getNavigationSections } from "config/navigation";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { logout } from "store/actions/session";
import { useProductBranding } from "store/selectors/branding";
import {
    useCurrentTenant,
    useCurrentUser,
    useFeature,
    useIsAllowed,
    useIsAllowedInWholeTenant,
} from "store/selectors/hooks";
import { useCanModifyRegionSettings, useTenantSettings } from "store/selectors/settings";
import { designSlice } from "store/slices/design";
import { ApplicationState } from "store/state";
import { isAllowed } from "../../util/permissions";
import { ReminderSidebarItem } from "../sidebar/reminder-sidebar-item";
import { DocLinkSidebarItem } from "./doc-link";
import { NgSidebarItem } from "./ng-sidebar-item";
import { AnnouncementsSidebarItem } from "./updates";

const NgAppLogo: React.FC = () => {
    const tenant = useCurrentTenant();

    const customTenantLogo = tenant?.hasLogo
        ? `/api/tenants/${tenant.tenantId}/logo`
        : null;

    if (customTenantLogo) {
        return (
            <Link
                to="/"
                className="mx-2 mt-2 mb-4 rounded-lg shadow-sm block min-h-24 h-24 py-2 overflow-hidden lg:rounded-lg lg:bg-white lg:shadow-sm lg:ring-1 lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10"
                style={{ lineHeight: 0, minHeight: "6rem" }}>
                <img
                    src={customTenantLogo}
                    className="object-contain w-full h-full"
                    alt="Tenant Logo"
                    style={{ display: "block", minHeight: "4rem" }}
                />
            </Link>
        );
    }

    return null;
};

type ExpandableSection =
    | "clients"
    | "tasks"
    | "files"
    | "reports"
    | "my-settings"
    | "agency-settings";

interface NgSidebarProps {
    mode: SidebarMode;
    expandable?: boolean;
}

export const NgSidebar: React.FC<NgSidebarProps> = ({ mode, expandable = true }) => {
    const dispatch = useDispatch();
    const user = useCurrentUser();
    const { pathname } = useLocation();
    const { settings } = useTenantSettings();
    const publicProfileSettingEnabled = settings.enablePublicProfile;
    const { branding } = useProductBranding();
    const sidebarBodyRef = useRef<HTMLDivElement>(null);

    const showReferrals = useSelector((state: ApplicationState) =>
        isAllowed(ActionType.ViewReference, state.permissions, state.region),
    );
    const canViewReports = useIsAllowed(ActionType.ViewReports);
    const viewAllEnabled = useIsAllowed(ActionType.ViewAllInvoices);
    const viewOwnEnabled = useIsAllowed(ActionType.ViewOwnInvoices);
    const viewCustomReportsEnabled = useIsAllowed(
        ActionType.AdvancedSearchExplicitExecute,
    );
    const csvDownloadEnabled = useIsAllowed(ActionType.ExportCsv);
    const referralSourceReportEnabled = useFeature("referralSourceReport");
    const stageConversionEnabled = useFeature("stageConversion");
    const timeInStageReportEnabled = useFeature("timeInStageReport");
    const homeCarePrototypeEnabled = useFeature("homeCarePrototype");
    const placementEnabled = useFeature("placement");

    const manageUsersAllowed = useIsAllowed(ActionType.SelfManageModifyUser);
    const manageIntegrationsAllowed = useIsAllowed(ActionType.ManageIntegrations);
    const manageWorkflowsAllowed = useIsAllowed(ActionType.CreateWorkflow);

    const outstandingInvoicesReportFeature = useFeature("outstandingInvoicesReport");
    const outstandingInvoicesReportEnabled =
        outstandingInvoicesReportFeature && viewOwnEnabled;
    const timeTrackingReportFeature = useFeature("trackTime");
    const { settings: tenantSettings } = useTenantSettings();
    const timeTrackingReportEnabled =
        timeTrackingReportFeature && tenantSettings.trackTimeEnabled && canViewReports;

    const franchiseManagementFeature = useFeature("franchiseManagement");
    const canUpdateFranchiseTerritories = useIsAllowedInWholeTenant(
        ActionType.UpdateFranchiseTerritory,
    );
    const franchiseManagementEnabled =
        franchiseManagementFeature && canUpdateFranchiseTerritories;
    const canViewFranchiseRoyaltyReport = useIsAllowedInWholeTenant(
        ActionType.ViewFranchiseRoyaltyReport,
    );
    const franchiseRoyaltyReportEnabled =
        franchiseManagementFeature && canViewFranchiseRoyaltyReport;

    const showAgencySettings = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const showRegionSettings = useCanModifyRegionSettings();

    const navigationSections = useMemo(
        () =>
            getNavigationSections({
                showReferrals,
                canViewReports,
                viewAllEnabled,
                viewOwnEnabled,
                viewCustomReportsEnabled,
                referralSourceReportEnabled,
                stageConversionEnabled,
                showAgencySettings,
                showRegionSettings,
                manageWorkflowsAllowed,
                manageUsersAllowed,
                manageIntegrationsAllowed,
                franchiseManagementEnabled,
                franchiseRoyaltyReportEnabled,
                csvDownloadEnabled,
                homeCarePrototypeEnabled,
                outstandingInvoicesReportEnabled,
                placementEnabled,
                timeTrackingReportEnabled,
                timeInStageReportEnabled,
            }),
        [
            showReferrals,
            canViewReports,
            viewAllEnabled,
            viewOwnEnabled,
            viewCustomReportsEnabled,
            referralSourceReportEnabled,
            stageConversionEnabled,
            showAgencySettings,
            showRegionSettings,
            manageWorkflowsAllowed,
            manageUsersAllowed,
            manageIntegrationsAllowed,
            franchiseManagementEnabled,
            franchiseRoyaltyReportEnabled,
            csvDownloadEnabled,
            homeCarePrototypeEnabled,
            outstandingInvoicesReportEnabled,
            placementEnabled,
            timeTrackingReportEnabled,
            timeInStageReportEnabled,
        ],
    );

    const [expandedSections, setExpandedSections] = useState<ExpandableSection[]>(() => {
        const initialExpanded: ExpandableSection[] = [];
        for (const section of navigationSections) {
            if (section.expandable && section.isActive(pathname)) {
                initialExpanded.push(section.id as ExpandableSection);
            }
        }
        return initialExpanded;
    });

    const toggleSection = useCallback((section: ExpandableSection) => {
        setExpandedSections(prev => {
            const collapsing = prev.includes(section);
            if (collapsing) {
                return prev.filter(s => s !== section);
            } else {
                return [...prev, section];
            }
        });
    }, []);

    const handleLogout = useCallback(() => {
        logout(dispatch);
    }, [dispatch]);

    const handleToggleSidebar = useCallback(() => {
        const newState = mode === "full" ? "collapsed" : "expanded";
        dispatch(designSlice.actions.setSidebarState(newState));
    }, [dispatch, mode]);

    return (
        <Sidebar mode={mode}>
            <div
                className={`flex items-center ${
                    mode === "full" ? "justify-start" : "justify-center"
                } p-2 max-lg:hidden`}>
                <button
                    onClick={handleToggleSidebar}
                    className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
                    title={
                        mode === "icons" ? "Expand sidebar" : "Collapse to icons only"
                    }>
                    {mode === "icons" ? (
                        <ChevronDoubleRightIcon className="w-4 h-4" />
                    ) : (
                        <ChevronDoubleLeftIcon className="w-4 h-4" />
                    )}
                </button>
            </div>
            <SidebarBody ref={sidebarBodyRef} mode={mode}>
                {mode === "full" && <NgAppLogo />}
                <SidebarSection mode={mode}>
                    {mode === "full" && <NgRegionToggle />}
                    <ReminderSidebarItem mode={mode} />
                    {navigationSections
                        .filter(
                            section =>
                                section.id !== "my-settings" &&
                                section.id !== "agency-settings" &&
                                section.visible !== false,
                        )
                        .map(section => {
                            const isExpanded = expandedSections.includes(
                                section.id as ExpandableSection,
                            );
                            const visibleItems = section.items.filter(
                                item => item.visible !== false,
                            );
                            const showExpandable =
                                mode === "full" && expandable && section.expandable;
                            const isDetailPage =
                                pathname.startsWith("/clients/show") ||
                                pathname.startsWith("/home-care-clients/show");
                            const isCurrent = isDetailPage
                                ? section.isActive(pathname)
                                : !isExpanded && section.isActive(pathname);

                            return (
                                <React.Fragment key={section.id}>
                                    <NgSidebarItem
                                        href={section.url}
                                        current={isCurrent}
                                        icon={section.icon}
                                        label={section.label}
                                        mode={mode}
                                        expandable={showExpandable}
                                        expanded={showExpandable ? isExpanded : undefined}
                                        onExpandToggle={
                                            showExpandable
                                                ? () =>
                                                      toggleSection(
                                                          section.id as ExpandableSection,
                                                      )
                                                : undefined
                                        }
                                    />
                                    {showExpandable && visibleItems.length > 0 && (
                                        <div className="max-lg:block lg:hidden">
                                            {isExpanded &&
                                                visibleItems.map(
                                                    ({
                                                        url,
                                                        activeMode = "exact",
                                                        label,
                                                    }) => (
                                                        <SidebarItem
                                                            key={url}
                                                            href={url}
                                                            current={
                                                                (activeMode === "exact"
                                                                    ? pathname === url
                                                                    : pathname.startsWith(
                                                                          url,
                                                                      )) ||
                                                                (url === "/pipeline" &&
                                                                    pathname === "/")
                                                            }
                                                            mode={mode}>
                                                            <SidebarLabel className="ml-8">
                                                                {label}
                                                            </SidebarLabel>
                                                        </SidebarItem>
                                                    ),
                                                )}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    <NgSidebarItem
                        href="/messaging"
                        current={pathname.startsWith("/messaging")}
                        icon={ChatBubbleLeftRightIcon}
                        label="Messaging"
                        mode={mode}
                    />
                </SidebarSection>

                <SidebarSpacer />
                <SidebarSection mode={mode}>
                    <AddClientSidebar mode={mode} />
                    <DocLinkSidebarItem mode={mode} />
                    <AnnouncementsSidebarItem mode={mode} />

                    {publicProfileSettingEnabled && placementEnabled ? (
                        <NgSidebarItem
                            href="/advisor-directory"
                            current={pathname.startsWith("/advisor-directory")}
                            icon={UserGroupIcon}
                            label="Advisor Directory"
                            mode={mode}
                        />
                    ) : null}
                    {(() => {
                        const settingsSections = navigationSections.filter(
                            s => s.id === "my-settings" || s.id === "agency-settings",
                        );
                        if (!settingsSections.length) return null;

                        return settingsSections.map(settingsSection => {
                            const isExpanded = expandedSections.includes(
                                settingsSection.id as ExpandableSection,
                            );
                            const visibleItems = settingsSection.items.filter(
                                item => item.visible !== false,
                            );
                            const showExpandable =
                                mode === "full" &&
                                expandable &&
                                settingsSection.expandable;

                            if (visibleItems.length === 0) {
                                return null;
                            }

                            return (
                                <React.Fragment key={settingsSection.id}>
                                    <NgSidebarItem
                                        className="lg:hidden"
                                        href={settingsSection.url}
                                        current={
                                            !showExpandable &&
                                            settingsSection.isActive(pathname)
                                        }
                                        icon={settingsSection.icon}
                                        label={settingsSection.label}
                                        mode={mode}
                                        expandable={showExpandable}
                                        expanded={showExpandable ? isExpanded : undefined}
                                        onExpandToggle={
                                            showExpandable
                                                ? () =>
                                                      toggleSection(
                                                          settingsSection.id as ExpandableSection,
                                                      )
                                                : undefined
                                        }
                                    />
                                    {showExpandable && (
                                        <div className="max-lg:block lg:hidden">
                                            {isExpanded &&
                                                visibleItems.map(item => (
                                                    <SidebarItem
                                                        key={item.url}
                                                        href={item.url}
                                                        current={pathname.startsWith(
                                                            item.url,
                                                        )}
                                                        mode={mode}>
                                                        <SidebarLabel className="ml-6">
                                                            {item.label}
                                                        </SidebarLabel>
                                                    </SidebarItem>
                                                ))}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        });
                    })()}
                    {csvDownloadEnabled && user && mode === "full" ? (
                        <NgSidebarItem
                            href="/exports"
                            current={pathname.startsWith("/exports")}
                            icon={TableCellsIcon}
                            label="CSV Exports"
                            mode={mode}
                            className="lg:hidden"
                        />
                    ) : null}
                    {user && mode === "full" ? (
                        <NgSidebarItem
                            href="#"
                            className="lg:hidden"
                            icon={ArrowRightStartOnRectangleIcon}
                            label="Sign out"
                            mode={mode}
                            onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                handleLogout();
                            }}
                        />
                    ) : null}
                </SidebarSection>
            </SidebarBody>

            {user ? (
                <SidebarFooter mode={mode} className="max-lg:hidden">
                    <Dropdown>
                        <DropdownButton as={SidebarItem}>
                            <span className="flex min-w-0 items-center gap-3">
                                <Avatar
                                    initials={getInitials(user.name)}
                                    className="size-10"
                                    square
                                    alt=""
                                />
                                {mode === "full" && (
                                    <span className="min-w-0">
                                        <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                                            {user.name}
                                        </span>
                                        <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                                            {user.email}
                                        </span>
                                    </span>
                                )}
                            </span>
                            {mode === "full" && <ChevronUpIcon />}
                        </DropdownButton>
                        <DropdownMenu className="min-w-52" anchor={"top start"}>
                            {csvDownloadEnabled ? (
                                <>
                                    <DropdownItem href="/exports">
                                        <TableCellsIcon />
                                        <DropdownLabel>CSV Export</DropdownLabel>
                                    </DropdownItem>
                                    <DropdownDivider />
                                </>
                            ) : null}
                            <DropdownItem href="/settings/account">
                                <UserIcon />
                                <DropdownLabel>My settings</DropdownLabel>
                            </DropdownItem>
                            {showAgencySettings ? (
                                <DropdownItem href="/settings/agency">
                                    <Cog8ToothIcon />
                                    <DropdownLabel>Agency settings</DropdownLabel>
                                </DropdownItem>
                            ) : null}
                            {showRegionSettings ? (
                                <DropdownItem href="/settings/region">
                                    <Cog8ToothIcon />
                                    <DropdownLabel>Office settings</DropdownLabel>
                                </DropdownItem>
                            ) : null}
                            <DropdownItem
                                onClick={() => {
                                    if (branding?.supportEmailAddress) {
                                        window.open(
                                            `mailto:${branding.supportEmailAddress}`,
                                            "_blank",
                                        );
                                    }
                                }}>
                                <LightBulbIcon />
                                <DropdownLabel>Share feedback</DropdownLabel>
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem
                                href="#"
                                onClick={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    handleLogout();
                                }}>
                                <ArrowRightStartOnRectangleIcon />
                                <DropdownLabel>Sign out</DropdownLabel>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </SidebarFooter>
            ) : null}
        </Sidebar>
    );
};
